import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle2, XCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, saveAuth } from "../lib/api";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("This verification link is missing its token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/verify-email", { params: { token } });
        saveAuth(data.token, data.user);
        setStatus("success");
        toast.success(`You're confirmed, ${data.user.name}!`);
        setTimeout(() => navigate(data.user.grade_level ? "/dashboard" : "/profile"), 1200);
      } catch (err) {
        setStatus("error");
        setErrorMsg(err?.response?.data?.detail || "This link is invalid or has expired.");
      }
    })();
  }, [token, navigate]);

  const onResend = async (e) => {
    e.preventDefault();
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: resendEmail });
      setResent(true);
    } catch {
      toast.error("Could not send a new link right now. Please try again.");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F9FF] bg-polka flex items-center justify-center p-5">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.5, duration: 0.6 }}
        className="w-full max-w-md"
      >
        {status === "verifying" && (
          <div className="card-soft text-center space-y-4" data-testid="verify-email-loading">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#FBBF24] items-center justify-center shadow-[0_8px_0_#B45309] mb-2 animate-pulse">
              <Sparkles className="w-10 h-10 text-[#1E1B4B]" strokeWidth={3} />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#1E1B4B]">Confirming…</h1>
            <p className="text-slate-600 font-semibold">Hang tight, we're activating your account.</p>
          </div>
        )}

        {status === "success" && (
          <div className="card-soft text-center space-y-4" data-testid="verify-email-success">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#4ADE80] items-center justify-center shadow-[0_8px_0_#15803D] mb-2">
              <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#1E1B4B]">You're all set!</h1>
            <p className="text-slate-600 font-semibold">Taking you to your dashboard…</p>
          </div>
        )}

        {status === "error" && (
          <div className="card-soft text-center space-y-4" data-testid="verify-email-error">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#FF6B6B] items-center justify-center shadow-[0_8px_0_#9F1239] mb-2">
              <XCircle className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h1 className="font-display text-2xl font-bold text-[#1E1B4B]">Link didn't work</h1>
            <p className="text-slate-600 font-semibold">{errorMsg}</p>

            {resent ? (
              <p className="text-sm text-slate-500">
                If that email is registered and unverified, a fresh link is on its way.
              </p>
            ) : (
              <form onSubmit={onResend} className="space-y-3 pt-2 text-left" data-testid="verify-email-resend-form">
                <label className="block text-sm font-bold text-[#1E1B4B] ml-2">Get a new link</label>
                <input
                  type="email" required value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
                  placeholder="you@school.com"
                  data-testid="verify-email-resend-input"
                />
                <button type="submit" disabled={resending} className="btn-clay w-full" data-testid="verify-email-resend-btn">
                  <Send className="w-5 h-5" strokeWidth={3} /> {resending ? "Sending…" : "Resend link"}
                </button>
              </form>
            )}

            <Link to="/login" className="block text-sm font-bold text-[#4D96FF] hover:underline pt-2" data-testid="verify-email-login-link">
              Back to login
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
